import { useEffect, useState } from "react";
import ImportedCashSummary from "./ImportedCashSummary";
import { api, idr } from "../../lib/api";
import { Wallet, Landmark, PiggyBank, ArrowRight, TrendingUp, TrendingDown, RefreshCw, ArrowLeftRight, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import LoadingState from "../../components/ui/loading-state";
import { formatTanggal } from "../../lib/tanggal";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";

// `GET /finance/kas/{id}/mutasi` mengurut tanggal desc lalu memotong
// `to_list(500)` — TANPA rentang tanggal default, jadi rekening yang sudah
// lama aktif bisa gampang menabrak batas ini (lihat accounts.py::kas_mutasi).
const MUTASI_BATAS_SERVER = 500;

const CATEGORY_STYLE = {
  bank:       { color: "sky",     icon: Landmark,  label: "Bank" },
  kas:        { color: "emerald", icon: Wallet,    label: "Kas Toko" },
  petty_cash: { color: "purple",  icon: PiggyBank, label: "Petty Cash" },
};

function AkunCard({ akun, onClick }) {
  const st = CATEGORY_STYLE[akun.category] || CATEGORY_STYLE.kas;
  const Icon = st.icon;
  return (
    <button
      onClick={onClick} data-testid={`kas-akun-${akun.code}`}
      className="text-left bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all group"
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg grid place-items-center bg-${st.color}-50 text-${st.color}-600`}>
          <Icon className="w-5 h-5" />
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-0.5">{st.label}</p>
      <p className="font-medium text-slate-900 text-sm truncate">{akun.name}</p>
      <p className={`font-display font-bold text-lg mt-2 ${akun.balance < 0 ? "text-rose-600" : "text-slate-900"}`}>{idr(akun.balance)}</p>
      <p className="text-[10px] font-mono text-slate-400 mt-1">{akun.code}</p>
    </button>
  );
}

function MutasiPanel({ akunId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Rentang tanggal (brief owner 29 Agu). Yang DIKIRIM ke server hanya nilai
  // yang sudah ditekan "Tampilkan" — bukan tiap ketikan. Endpointnya memotong
  // di 500 baris, jadi menyaring di layar akan melewatkan mutasi lama tanpa
  // tanda apa pun; menyaringnya di server juga membuat 500 baris itu jatuh di
  // rentang yang benar-benar diminta.
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [terpakai, setTerpakai] = useState({ dari: "", sampai: "" });
  const load = () => {
    setLoading(true);
    setLoadError(null);
    const p = new URLSearchParams();
    if (terpakai.dari) p.set("date_from", terpakai.dari);
    if (terpakai.sampai) p.set("date_to", terpakai.sampai);
    const qs = p.toString();
    api.get(`/finance/kas/${akunId}/mutasi${qs ? "?" + qs : ""}`)
      .then(r => setData(r.data))
      .catch((e) => { setLoadError(e); toast.error("Gagal memuat mutasi"); })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [akunId, terpakai]);

  const tampilkan = () => {
    if (dari && sampai && dari > sampai) {
      toast.error("Tanggal 'dari' melewati tanggal 'sampai'");
      return;
    }
    setTerpakai({ dari, sampai });
  };
  const reset = () => { setDari(""); setSampai(""); setTerpakai({ dari: "", sampai: "" }); };
  // Urutan bawaan (tanggal desc dari backend) tetap sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedMutasi = sortRows(data?.mutasi || [], sortKey, sortDir, {
    tanggal: (m) => m.tanggal || "",
    deskripsi: (m) => m.deskripsi || "",
    masuk: (m) => m.masuk || 0,
    keluar: (m) => m.keluar || 0,
  });
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={2} data-testid="loading-mutasi">
      {data && <>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Mutasi Akun</p>
          <p className="font-display font-bold text-lg text-slate-900">{data.akun.name}</p>
          <p className="text-sm text-emerald-700 font-semibold mt-0.5">Saldo: {idr(data.akun.balance)}</p>
        </div>
        <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900" data-testid="mutasi-close">Tutup</button>
      </div>
      {/* Sortir berdasarkan periode tanggal (brief owner 29 Agu). */}
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-end gap-3"
           data-testid="kas-filter-periode">
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Dari Tanggal</label>
          <input type="date" value={dari} onChange={e => setDari(e.target.value)}
            data-testid="kas-dari" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Sampai Tanggal</label>
          <input type="date" value={sampai} onChange={e => setSampai(e.target.value)}
            data-testid="kas-sampai" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <button onClick={tampilkan} data-testid="kas-tampilkan"
          className="h-9 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
          Tampilkan
        </button>
        {(terpakai.dari || terpakai.sampai) && (
          <button onClick={reset} data-testid="kas-reset"
            className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm">
            Reset
          </button>
        )}
        {(terpakai.dari || terpakai.sampai) && (
          <p className="text-[11px] text-slate-500">
            Menampilkan {terpakai.dari || "awal"} s/d {terpakai.sampai || "sekarang"}
          </p>
        )}
      </div>
      <BatasDaftarNotice shown={data.mutasi.length} batas={MUTASI_BATAS_SERVER} />
      <div className="max-h-[500px] overflow-y-auto">
        {data.mutasi.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">Belum ada mutasi</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="kas-mutasi-sort-tanggal" />
                <SortTh label="Keterangan" sortKeyName="deskripsi" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="kas-mutasi-sort-deskripsi" />
                <SortTh label="Masuk" sortKeyName="masuk" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="kas-mutasi-sort-masuk" />
                <SortTh label="Keluar" sortKeyName="keluar" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="kas-mutasi-sort-keluar" />
              </tr>
            </thead>
            <tbody>
              {sortedMutasi.map((m, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-2 text-slate-600 font-mono text-xs">{formatTanggal(m.tanggal)}</td>
                  <td className="px-4 py-2">
                    <p className="text-slate-800 text-sm">{m.deskripsi}</p>
                    <p className="text-[10px] text-slate-400">{m.kategori}</p>
                  </td>
                  <td className="px-4 py-2 text-right text-emerald-700 font-mono font-semibold">{m.masuk > 0 ? idr(m.masuk) : "-"}</td>
                  <td className="px-4 py-2 text-right text-rose-700 font-mono font-semibold">{m.keluar > 0 ? idr(m.keluar) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>}
      </LoadingState>
    </div>
  );
}

export default function FinanceKasBank() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedAkun, setSelectedAkun] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/finance/kas/summary");
      setSummary(r.data);
    } catch (e) {
      setLoadError(e);
      toast.error(e?.response?.data?.detail || "Gagal memuat");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const banks = summary?.akun.filter(a => a.category === "bank") || [];
  const kass = summary?.akun.filter(a => a.category === "kas") || [];
  const petty = summary?.akun.filter(a => a.category === "petty_cash") || [];

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-kas-bank">
      <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={3} data-testid="loading-kas-bank">
      {summary && <>
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Kas & Bank</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Multi-Rekening</h1>
          <p className="text-sm text-slate-600 mt-1">{summary.akun.length} akun · Grand Total {idr(summary.grand_total)}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="kas-refresh" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={() => nav("/finance/catat/transfer")} data-testid="kas-transfer" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium">
            <ArrowLeftRight className="w-4 h-4" /> Transfer Antar Akun
          </button>
        </div>
      </div>

      <ImportedCashSummary />
      {/* Today snapshot */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Bank</p>
          <p className="font-display text-2xl font-bold text-sky-700 mt-2">{idr(summary.total_bank)}</p>
          <p className="text-[11px] text-slate-500 mt-1">{banks.length} rekening</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Kas Toko</p>
          <p className="font-display text-2xl font-bold text-emerald-700 mt-2">{idr(summary.total_kas)}</p>
          <p className="text-[11px] text-slate-500 mt-1">{kass.length + petty.length} akun</p>
        </div>
        <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white border border-slate-900 rounded-xl p-5 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-white/60">Hari Ini</p>
          <p className="font-display text-lg font-bold mt-2 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> {idr(summary.total_masuk_hari_ini)}</p>
          <p className="font-display text-lg font-bold flex items-center gap-2 mt-1"><TrendingDown className="w-4 h-4 text-rose-400" /> {idr(summary.total_keluar_hari_ini)}</p>
        </div>
      </div>

      {selectedAkun && (
        <MutasiPanel akunId={selectedAkun} onClose={() => setSelectedAkun(null)} />
      )}

      {!selectedAkun && (
        <>
          {banks.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-base text-slate-900 mb-3 flex items-center gap-2">
                <Landmark className="w-4 h-4 text-sky-600" /> Rekening Bank
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {banks.map(a => <AkunCard key={a.id} akun={a} onClick={() => setSelectedAkun(a.id)} />)}
              </div>
            </div>
          )}
          {kass.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-base text-slate-900 mb-3 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-600" /> Kas Toko
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {kass.map(a => <AkunCard key={a.id} akun={a} onClick={() => setSelectedAkun(a.id)} />)}
              </div>
            </div>
          )}
          {petty.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-base text-slate-900 mb-3 flex items-center gap-2">
                <PiggyBank className="w-4 h-4 text-purple-600" /> Petty Cash
                <Link to="/finance/petty-cash" className="ml-auto text-xs font-medium text-emerald-700 hover:underline inline-flex items-center gap-1">Kelola Petty Cash <ArrowRight className="w-3 h-3" /></Link>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {petty.map(a => <AkunCard key={a.id} akun={a} onClick={() => setSelectedAkun(a.id)} />)}
              </div>
            </div>
          )}
        </>
      )}
      </>}
      </LoadingState>
    </div>
  );
}
